import React, { useState, useEffect } from 'react';
import sanctumApi from '../services/api';
import './Waitlist.css';

const Waitlist = () => {
  const [waitlistUsers, setWaitlistUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');

  const fetchWaitlist = async (page = 1, search = '') => {
    setLoading(true);
    setError('');
    try {
      const response = await sanctumApi.getWaitlist(page, 50, search);
      // Don't re-sort, use backend order
      setWaitlistUsers(response.users);
      setTotalPages(response.totalPages);
      setTotal(response.total);
      setCurrentPage(response.page);
      
      // Debug: log first few entries to check order
      console.log('Waitlist data from backend:', response.users.slice(0, 5).map(u => ({
        email: u.email,
        createdAt: u.createdAt,
        date: new Date(u.createdAt)
      })));
    } catch (error) {
      console.error('Error fetching waitlist:', error);
      setError('Failed to load waitlist users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch on page change when not searching
    if (!searchTerm) {
      fetchWaitlist(currentPage, searchTerm);
    }
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Debounced search - always resets to page 1
    const timeoutId = setTimeout(() => {
      fetchWaitlist(1, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    // Show more precise time including seconds for debugging
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const handleExport = () => {
    const csv = [
      ['Email', 'Joined At'],
      ...waitlistUsers.map(user => [user.email, formatDate(user.createdAt || user.joinedAt)])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `waitlist_${new Date().toISOString().split('T')[0]}.csv`);
    a.click();
  };

  return (
    <div className="waitlist-page">
      <div className="page-header">
        <h1>Waitlist Users</h1>
        <div className="header-actions">
          <span className="user-count">{total} users</span>
          <button className="export-btn" onClick={handleExport} disabled={loading}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search by email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {error && <div className="error-message">{error}</div>}
      
      <div className="waitlist-table">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Email</th>
                <th>Joined Date</th>
              </tr>
            </thead>
            <tbody>
              {waitlistUsers.length > 0 ? (
                waitlistUsers.map((user, index) => (
                  <tr key={user._id}>
                    <td>{(currentPage - 1) * 50 + index + 1}</td>
                    <td>{user.email}</td>
                    <td>{formatDate(user.createdAt || user.joinedAt)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="no-data">
                    {searchTerm ? 'No users found matching your search' : 'No users in waitlist yet'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1 || loading}
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages || loading}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Waitlist;